import type { MouseEvent } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { PencilSimpleIcon } from "@phosphor-icons/react";
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import { z } from "zod";

import { Button, Select, toastError, toastSuccess } from "@alpacahq/ui/old";

import { useMutationRequest } from "~/v2/api/hooks";
import { patchDetails } from "~/v2/api/rest/account";
import { SectionDropdown } from "../../../../components/common/SectionDropdown";
import type { IRAOnboardingProps } from "..";
import { SavedInput } from "./SavedInput";

const schema = z.object({
  is_affiliated_exchange_or_finra: z.boolean({ error: "Required" }),
  is_control_person: z.boolean({ error: "Required" }),
  is_politically_exposed: z.boolean({ error: "Required" }),
  immediate_family_exposed: z.boolean({ error: "Required" }),
});

type AffiliationsSchema = z.infer<typeof schema>;

const yesNoOrUnset = (value: boolean | null | undefined) =>
  value == null ? "—" : value ? "Yes" : "No";

export interface AffiliationsProps {
  tradeAccount: IRAOnboardingProps["tradeAccount"];
  tradeAccountDetails: IRAOnboardingProps["tradeAccountDetails"];
  refetchTradeAccountDetails: () => void;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export const Affiliations = ({
  tradeAccount,
  tradeAccountDetails,
  refetchTradeAccountDetails,
  isEditing,
  setIsEditing,
  isOpen,
  setIsOpen,
}: AffiliationsProps) => {
  const {
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<AffiliationsSchema>({
    resolver: zodResolver(schema),
    values: {
      is_affiliated_exchange_or_finra:
        tradeAccountDetails.is_affiliated_exchange_or_finra ?? undefined,
      is_control_person: tradeAccountDetails.is_control_person ?? undefined,
      is_politically_exposed:
        tradeAccountDetails.is_politically_exposed ?? undefined,
      immediate_family_exposed:
        tradeAccountDetails.immediate_family_exposed ?? undefined,
    } as AffiliationsSchema,
    mode: "onChange",
  });

  const saveDetailsMutation = useMutationRequest(
    (data: AffiliationsSchema) =>
      patchDetails(tradeAccount.id, {
        is_affiliated_exchange_or_finra: data.is_affiliated_exchange_or_finra,
        is_control_person: data.is_control_person,
        is_politically_exposed: data.is_politically_exposed,
        immediate_family_exposed: data.immediate_family_exposed,
      }),
    {
      onSuccess: () => {
        refetchTradeAccountDetails();
        setIsEditing(false);
        toastSuccess("Affiliations updated");
      },
      onError: () => {
        toastError("Failed to update affiliations");
      },
    },
  );

  const onToggleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (!isOpen) {
      setIsOpen(true);
      return;
    }
    // only close when not editing
    if (isEditing) {
      return;
    }

    setIsOpen(false);
  };

  const onEditClicked = (event: MouseEvent<HTMLButtonElement>) => {
    // When button re-renders as Save button after this click it triggers form submission even with type=button on this Edit button
    event.preventDefault();
    setIsEditing(true);
    setIsOpen(true);
  };

  const onSubmitValid: SubmitHandler<AffiliationsSchema> = (data) => {
    if (isEditing) {
      saveDetailsMutation.mutate(data);
    }
  };

  const onSubmitInvalid: SubmitErrorHandler<AffiliationsSchema> = () => {
    toastError("Please fill in all fields");
  };

  const isControlPerson = watch("is_control_person");
  const isAffiliatedExchangeOrFinra = watch("is_affiliated_exchange_or_finra");
  const isPoliticallyExposed = watch("is_politically_exposed");
  const isImmediateFamilyExposed = watch("immediate_family_exposed");

  return (
    <form onSubmit={handleSubmit(onSubmitValid, onSubmitInvalid)}>
      <SectionDropdown
        title="Affiliations"
        isOpen={isOpen}
        onToggleOpen={onToggleOpen}
        className="rounded-lg border border-gray-200 bg-white p-4"
        icon={
          !isEditing ? (
            <Button
              variant="v2/ghost"
              size="v2/small"
              type="button"
              onClick={onEditClicked}
            >
              <PencilSimpleIcon size={20} className="fill-gray-700" />
            </Button>
          ) : (
            <Button
              variant="v2/ghost"
              size="v2/small"
              type="submit"
              disabled={saveDetailsMutation.isPending}
            >
              Save
            </Button>
          )
        }
      >
        {isEditing ? (
          <div className="flex flex-col gap-4">
            <Select
              label="Are you an officer or 10% or greater shareholder of a publicly traded company, subject to the US Securities Exchange Act 1934?"
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ]}
              value={isControlPerson}
              onChange={(value) => {
                setValue("is_control_person", value);
              }}
              errorMsg={errors.is_control_person?.message}
              status={errors.is_control_person?.message ? "error" : "neutral"}
            />
            <Select
              label="Are you affiliated with or employed by a stock exchange, regulatory body, member firm of an exchange, FINRA or a municipal securities broker-dealer?"
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ]}
              value={isAffiliatedExchangeOrFinra}
              onChange={(value) => {
                setValue("is_affiliated_exchange_or_finra", value);
              }}
              errorMsg={errors.is_affiliated_exchange_or_finra?.message}
              status={
                errors.is_affiliated_exchange_or_finra?.message
                  ? "error"
                  : "neutral"
              }
            />
            <Select
              label="Are you currently or formerly a Politically Exposed Person or Public Official?"
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ]}
              value={isPoliticallyExposed}
              onChange={(value) => {
                setValue("is_politically_exposed", value);
              }}
              errorMsg={errors.is_politically_exposed?.message}
              status={
                errors.is_politically_exposed?.message ? "error" : "neutral"
              }
            />
            <Select
              label="Do any of the above apply to an immediate family member?"
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ]}
              value={isImmediateFamilyExposed}
              onChange={(value) => {
                setValue("immediate_family_exposed", value);
              }}
              errorMsg={errors.immediate_family_exposed?.message}
              status={
                errors.immediate_family_exposed?.message ? "error" : "neutral"
              }
            />
          </div>
        ) : (
          <div className="flex flex-col">
            <SavedInput
              label="Public Company Shareholder/Officer"
              value={yesNoOrUnset(tradeAccountDetails.is_control_person)}
            />
            <SavedInput
              label="Industry Affiliations"
              value={yesNoOrUnset(
                tradeAccountDetails.is_affiliated_exchange_or_finra,
              )}
            />
            <SavedInput
              label="Politically Exposed Person (PEP)"
              value={yesNoOrUnset(tradeAccountDetails.is_politically_exposed)}
            />
            <SavedInput
              label="Family Member Affiliations"
              value={yesNoOrUnset(tradeAccountDetails.immediate_family_exposed)}
            />
          </div>
        )}
      </SectionDropdown>
    </form>
  );
};
